import React, { useState, useEffect, useRef } from 'react';
import Header from './components/home/Header';
import Footer from './components/home/Footer';

// Main pages
import Home from './components/Home';
import About from './components/pages/About';
import Courses from './components/pages/Courses';
import CourseDetails from './components/pages/CourseDetails';
import VideoEditingCourse from './components/pages/VideoEditingCourse';
import SuccessStories from './components/pages/SuccessStories';
import Blog from './components/pages/Blog';
import MyLearning from './components/pages/MyLearning';
import FAQ from './components/pages/FAQ'; 

// Legal pages
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsConditions from './components/pages/TermsConditions';
import RefundPolicy from './components/pages/RefundPolicy';

// Modals
import Login from './components/pages/Login';
import Register from './components/pages/Register';
import ProfileModal from './components/pages/ProfileModal';
import { checkUserSession, getUserProfile, logoutUser } from './api/userAuthApi';
import { trackEngagement } from './api/engagementApi';
import { courseTitleToSlug } from './api/courseSlug';
import { trackAnalyticsEvent } from './monitoring';

const ROUTE_MAP = {
  '#about': 'about',
  '#courses': 'courses',
  '#video-editing-course': 'video-editing',
  '#premiere-pro': 'video-editing',
  '#stories': 'stories',
  '#success-stories': 'stories',
  '#blog': 'blog',
  '#mylearning': 'mylearning',
  '#dashboard': 'mylearning',
  '#faq': 'faq',      
  '#faqs': 'faq',
  '#FAQs': 'faq',
  '#privacy-policy': 'privacy',
  '#privacy': 'privacy',
  '#terms-and-conditions': 'terms',
  '#terms': 'terms',
  '#refund-policy': 'refund',
  '#refund': 'refund'
};

const AUTH_HASHES = ['#login', '#register', '#signup'];
const LOGIN_RETURN_HASH_KEY = 'cd_login_return_hash';

export default function App() {
  const [route, setRoute] = useState('home');
  const [authModal, setAuthModal] = useState(null); // null | 'login' | 'register'
  const [signupMobile, setSignupMobile] = useState('');
  const [signupRegistrationToken, setSignupRegistrationToken] = useState('');
  const [googleSignupToken, setGoogleSignupToken] = useState('');
  const [googleSignupProfile, setGoogleSignupProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('beyond-school-skills');
  const [selectedCourseByTitle, setSelectedCourseByTitle] = useState(false);
  const isGoogleCallbackRef = useRef(false);
  const skipNextReturnHashStoreRef = useRef(false);
  const returnHashRef = useRef(
    (() => {
      const currentHash = window.location.hash;
      const currentRouteHash = currentHash.split('?')[0];

      if (currentHash && !AUTH_HASHES.includes(currentRouteHash) && currentRouteHash !== '#google-callback') {
        return currentHash;
      }

      const savedHash = window.sessionStorage.getItem(LOGIN_RETURN_HASH_KEY);
      const savedRouteHash = savedHash?.split('?')[0];
      return savedHash?.startsWith('#') &&
        !AUTH_HASHES.includes(savedRouteHash) &&
        savedRouteHash !== '#google-callback'
        ? savedHash
        : '#home';
    })(),
  );

  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('cd_auth_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      localStorage.removeItem('cd_auth_user');
      localStorage.removeItem('cd_user_token');
      return null;
    }
  });

  const handleLoginSuccess = (userData, accessToken) => {
    setUser(userData);
    localStorage.setItem('cd_auth_user', JSON.stringify(userData));
    if (accessToken) localStorage.setItem('cd_user_token', accessToken);
    setSignupRegistrationToken('');
    setGoogleSignupToken('');
    setGoogleSignupProfile(null);
    setAuthModal(null);
    const currentHash = window.location.hash;
    const shouldRestoreReturnHash =
      AUTH_HASHES.includes(currentHash.split('?')[0]) ||
      isGoogleCallbackRef.current ||
      currentHash !== returnHashRef.current;

    if (shouldRestoreReturnHash && currentHash !== returnHashRef.current) {
      skipNextReturnHashStoreRef.current = true;
    }

    if (
      AUTH_HASHES.includes(currentHash.split('?')[0]) ||
      isGoogleCallbackRef.current ||
      currentHash !== returnHashRef.current
    ) {
      window.location.hash = returnHashRef.current;
    }
    isGoogleCallbackRef.current = false;
    window.sessionStorage.removeItem(LOGIN_RETURN_HASH_KEY);
  };

  useEffect(() => {
    const [callbackRoute, queryString = ''] = window.location.hash.slice(1).split('?');
    if (callbackRoute !== 'google-callback') return;

    isGoogleCallbackRef.current = true;
    const params = new URLSearchParams(queryString);
    const accessToken = params.get('accessToken');
    const signupToken = params.get('signupToken');
    const googleError = params.get('googleError');
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#home`,
    );

    if (signupToken) {
      setGoogleSignupToken(signupToken);
      setGoogleSignupProfile({
        name: params.get('signupName') || '',
        email: params.get('signupEmail') || '',
        image: params.get('signupImage') || '',
      });
      setAuthModal('register');
      return;
    }

    if (!accessToken) {
      const message = googleError || 'Google sign-in could not be completed.';
      setAuthModal('login');
      window.location.hash = `#login?googleError=${encodeURIComponent(message)}`;
      return;
    }

    localStorage.setItem('cd_user_token', accessToken);
    getUserProfile()
      .then(({ user: userData }) => {
        trackAnalyticsEvent('login', { method: 'google' });
        handleLoginSuccess(userData, accessToken);
      })
      .catch((error) => {
        localStorage.removeItem('cd_user_token');
        const message = error.message || 'Could not load your account after Google sign-in.';
        setAuthModal('login');
        window.location.hash = `#login?googleError=${encodeURIComponent(message)}`;
      });
  }, []);

  useEffect(() => {
    const handleInvalidSession = () => {
      localStorage.removeItem('cd_auth_user');
      localStorage.removeItem('cd_user_token');
      setUser(null);
      setShowProfile(false);
      setSignupRegistrationToken('');
      setAuthModal('login');
      trackAnalyticsEvent('logout', { reason: 'session_invalid' });
      window.location.hash = '#login?sessionExpired=1';
    };

    window.addEventListener('cd:user-account-deleted', handleInvalidSession);
    window.addEventListener('cd:user-session-invalid', handleInvalidSession);
    return () => {
      window.removeEventListener('cd:user-account-deleted', handleInvalidSession);
      window.removeEventListener('cd:user-session-invalid', handleInvalidSession);
    };
  }, []);

  useEffect(() => {
    if (!user || !localStorage.getItem('cd_user_token')) return undefined;

    const checkSession = () => {
      checkUserSession().catch(() => {
        // API failures are reported by the shared fetch monitor; only a deleted-account
        // response should clear the local session.
      });
    };

    checkSession();
    const intervalId = window.setInterval(checkSession, 3000);
    return () => window.clearInterval(intervalId);
  }, [user]);

  const closeAuthModal = () => {
    setAuthModal(null);
    setSignupRegistrationToken('');
    setGoogleSignupToken('');
    setGoogleSignupProfile(null);
    if (AUTH_HASHES.includes(window.location.hash.split('?')[0])) {
      window.location.hash = returnHashRef.current;
    }
    isGoogleCallbackRef.current = false;
  };

  const handleUserUpdate = (updatedData) => {
    setUser((previousUser) => {
      const mergedUser = {
        ...(previousUser || {}),
        ...(updatedData || {}),
        socialLinks: {
          ...(previousUser?.socialLinks || {}),
          ...(updatedData?.socialLinks || {}),
        },
      };
      localStorage.setItem('cd_auth_user', JSON.stringify(mergedUser));
      return mergedUser;
    });
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('cd_user_token');
    try {
      await logoutUser(token);
    } catch (error) {
      console.warn('User logout request failed:', error.message);
    }
    setUser(null);
    localStorage.removeItem('cd_auth_user');
    localStorage.removeItem('cd_user_token');
    trackAnalyticsEvent('logout');
    window.location.hash = '#home';
  };

  const handleCourseSelect = (course) => {
    const courseId = typeof course === 'object' ? course.id : course;
    const courseSlug = typeof course === 'object'
      ? courseTitleToSlug(course.title)
      : '';
    const courseType = typeof course === 'object' ? course.courseType : '';
    const normalizedCourseType = String(courseType || '').trim().toLowerCase();
    const isVideoCourse = ['2', 'video'].includes(normalizedCourseType)
      || normalizedCourseType.includes('video')
      || courseId === 'premiere-pro-mastery';

    trackEngagement('course', 'select', { contentId: courseId });
    trackAnalyticsEvent('course_select', {
      course_id: String(courseId || ''),
      course_type: isVideoCourse ? 'video' : 'standard',
    });

    if (isVideoCourse) {
      setSelectedCourse(courseSlug);
      setSelectedCourseByTitle(true);
      window.location.hash = `#video-editing-course?course=${encodeURIComponent(courseSlug)}`;
    } else {
      setSelectedCourse(courseSlug);
      setSelectedCourseByTitle(true);
      window.location.hash = `#course-details?course=${encodeURIComponent(courseSlug)}`;
    }
  };

  // Determine if active route requires floating enroll bar spacer
  const hasStickyBar = route === 'course-details' || route === 'video-editing';

  // Measure floating bar height dynamically via RAF to prevent ResizeObserver loop errors
  useEffect(() => {
    if (!hasStickyBar) {
      document.documentElement.style.removeProperty('--floating-bar-height');
      return;
    }

    let rafId = null;

    const updateHeight = () => {
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        const floatingEl = document.querySelector('.cd-mobile-sticky-enroll-bar, .ve-mobile-floating-bar');
        if (floatingEl) {
          const height = floatingEl.getBoundingClientRect().height;
          document.documentElement.style.setProperty('--floating-bar-height', `${height}px`);
        }
      });
    };

    const floatingEl = document.querySelector('.cd-mobile-sticky-enroll-bar, .ve-mobile-floating-bar');
    let resizeObserver = null;

    if (floatingEl && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(floatingEl);
    }

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--floating-bar-height');
    };
  }, [route, hasStickyBar]);

  // Lock background scroll when any modal is open
  useEffect(() => {
    const isModalOpen = Boolean(authModal || showProfile);
    document.body.style.overflow = isModalOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [authModal, showProfile]);

  // Hash route listener & modal hash trigger
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      const [routeHash, queryString = ''] = hash.split('?');
      const params = new URLSearchParams(queryString);
      const courseSlug = params.get('course');
      const courseId = courseSlug || params.get('id');

      if (courseId) {
        setSelectedCourse(courseId);
        setSelectedCourseByTitle(Boolean(courseSlug));
      } else if (routeHash === '#course-details') {
        setSelectedCourse('');
        setSelectedCourseByTitle(false);
      }

      if (routeHash === '#login') {
        setAuthModal('login');
        return;
      }
      if (routeHash === '#register' || routeHash === '#signup') {
        setAuthModal('register');
        return;
      }

      if (!isGoogleCallbackRef.current && routeHash !== '#google-callback') {
        if (skipNextReturnHashStoreRef.current) {
          skipNextReturnHashStoreRef.current = false;
        } else {
          returnHashRef.current = hash || '#home';
          window.sessionStorage.setItem(LOGIN_RETURN_HASH_KEY, returnHashRef.current);
        }
      }

      if (
        (routeHash === '#mylearning' || routeHash === '#dashboard') &&
        !localStorage.getItem('cd_user_token')
      ) {
        window.location.hash = '#home';
        return;
      }

      if (routeHash === '#course-details') {
        setRoute('course-details');
        trackEngagement('page', 'view', { contentId: 'course-details' });
        trackAnalyticsEvent('page_view', { page_name: 'course-details' });
      } else {
        const nextRoute = ROUTE_MAP[routeHash] || 'home';
        setRoute(nextRoute);
        trackEngagement('page', 'view', { contentId: nextRoute });
        trackAnalyticsEvent('page_view', { page_name: nextRoute });
      }
      window.scrollTo(0, 0);
    };

    window.addEventListener('hashchange', onHashChange);
    onHashChange();

    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const renderView = () => {
    switch (route) {
      case 'about':
        return <About />;
      case 'courses':
        return <Courses onSelectCourse={handleCourseSelect} />;
      case 'video-editing':
        return (
          <VideoEditingCourse
            courseId={selectedCourse}
            courseByTitle={selectedCourseByTitle}
            currentUser={user}
          />
        );
      case 'course-details':
        return (
          <CourseDetails
            courseId={selectedCourse}
            courseByTitle={selectedCourseByTitle}
            currentUser={user}
          />
        );
      case 'stories':
        return <SuccessStories />;
      case 'blog':
        return <Blog />;
      case 'mylearning':
        return <MyLearning currentUser={user} onSelectCourse={handleCourseSelect} />;
      case 'faq':
        return <FAQ />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsConditions />;
      case 'refund':
        return <RefundPolicy />;
      case 'home':
      default:
        return <Home onSelectCourse={handleCourseSelect} />;
    }
  };

  return (
    <div className="app-container" style={{ position: 'relative', minHeight: '100vh' }}>
      <Header
        onLoginClick={() => setAuthModal('login')}
        currentUser={user}
        onLogout={handleLogout}
        onProfileClick={() => setShowProfile(true)}
      />

      {renderView()}

      {/* Conditionally passes spacer flag only for course pages */}
      <Footer hasBottomSpacer={hasStickyBar} />

      {/* USER PROFILE MODAL */}
      {showProfile && (
        <ProfileModal
          currentUser={user}
          onClose={() => setShowProfile(false)}
          onUpdateUser={handleUserUpdate}
        />
      )}

      {/* AUTH MODAL DIALOG */}
      {authModal && (
        <div
          className="auth-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            boxSizing: 'border-box'
          }}
        >
          <div
            className="auth-modal-backdrop"
            onClick={closeAuthModal}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(4, 17, 47, 0.85)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              zIndex: 1
            }}
          />
          <div
            className="auth-modal-dialog"
            style={{
              position: 'relative',
              zIndex: 10,
              width: '100%',
              maxWidth: '920px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {authModal === 'login' ? (
              <Login
              onClose={closeAuthModal}
                onSignupRedirect={(mobile = '', registrationToken = '') => {
                  setSignupMobile(mobile);
                  setSignupRegistrationToken(registrationToken);
                  setAuthModal('register');
                }}
                onLoginSuccess={(...args) => {
                  trackAnalyticsEvent('login', { method: 'mobile_otp' });
                  handleLoginSuccess(...args);
                }}
              />
            ) : (
              <Register
              onClose={closeAuthModal}
                onLoginClick={() => {
                  setSignupRegistrationToken('');
                  setGoogleSignupToken('');
                  setGoogleSignupProfile(null);
                  setAuthModal('login');
                }}
                onRegisterSuccess={(...args) => {
                  trackAnalyticsEvent('sign_up', {
                    method: googleSignupToken ? 'google' : 'mobile_otp',
                  });
                  handleLoginSuccess(...args);
                }}
                initialMobile={signupMobile}
                initialRegistrationToken={signupRegistrationToken}
                googleSignupToken={googleSignupToken}
                initialGoogleProfile={googleSignupProfile}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}